import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  Braces,
  CheckCircle2,
  ChevronRight,
  CloudUpload,
  FileSpreadsheet,
  Info,
  Loader2,
  RefreshCw,
  Target,
  Trophy,
  Undo2,
  Users,
} from "lucide-react";
import ConfirmDialog from "../../components/ConfirmDialog";
import { AdminTopbar } from "../../layouts/AdminLayout";
import { Badge, Button, Pagination, Tabs } from "../../components/ui";
import { adminSession, apiDelete, apiUpload, useApi } from "../../api/client";
import { Competition, ImportRecord, MatchInfo } from "../../data/types";
import { usePortalLink } from "../../utils/portalLink";

/*
 * Only the Excel workbook import is in service. The JSON shotslog and flat-CSV
 * paths still exist end to end on the backend (POST /admin/import/shotslog
 * accepts .json and .csv) — they are hidden here until they are needed. To bring
 * them back, restore the two entries below and the `TAB_ORDER` line; the rest of
 * this page already handles all three.
 */
const TABS = {
  workbook: "Shoot Workbook (Excel)",
  // json: "JSON Shotslog",
  // csv: "CSV Rows",
};

// Hidden while only the workbook import is offered — a single tab reads as a
// heading rather than a choice.
const SHOW_TABS = Object.keys(TABS).length > 1;


export function ImportStatusBadge({ status }: { status: string }) {
  const tone = status === "Completed" ? "active" : status === "Failed" ? "failed" : status === "In Progress" ? "inprogress" : "pending";
  return <Badge tone={tone as any}>{status}</Badge>;
}

/** Backend response for either import path — workbook fields are absent for JSON/CSV. */
interface ImportResult {
  importId: string;
  kind?: "workbook";
  status: string;
  shotsImported: number;
  stringsCreated: number;
  stringIds?: string[];
  matchesUpdated?: string[];
  eventsUpdated?: string[];
  errors: string[];
  competition?: { id: string; name: string; eid: string | null; status: string };
  events?: { id: string; name: string; date: string }[];
  matches?: { id: string; name: string; distance: string; shots: number }[];
  shooters?: { sid: string; name: string; classes: string[]; grade: string }[];
  counts?: {
    events: number;
    matches: number;
    shooters: number;
    strings: number;
    shots: number;
    blocksRead: number;
    blocksSkipped: number;
    unresolvedShooters: number;
  };
  warnings?: string[];
}

function Stat({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div className="rounded-lg bg-white p-3 text-center">
      <div className="text-2xl font-extrabold text-ink">{value}</div>
      <div className="text-xs leading-tight text-muted">{label}</div>
    </div>
  );
}

/** What the workbook actually created, with links straight into the public portal. */
function WorkbookReport({ result }: { result: ImportResult }) {
  const link = usePortalLink();
  const c = result.counts!;
  const comp = result.competition!;
  return (
    <>
      {/* 3 across at most — this panel is narrow and six tiles clip their labels */}
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat value={c.shooters} label="Competitors" />
        <Stat value={c.events} label="Days" />
        <Stat value={c.matches} label="Matches" />
        <Stat value={c.strings} label="Strings" />
        <Stat value={c.shots} label="Shots" />
        <Stat value={c.blocksSkipped} label="Blocks Skipped" />
      </div>

      <div className="mt-3 rounded-lg bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Trophy size={16} className="text-primary" />
          <Link to={link(`/competitions/${comp.id}`)} className="text-[15px] font-bold text-primary hover:underline">
            {comp.name}
          </Link>
          <Badge tone={comp.status === "COMPLETED" ? "active" : "inprogress"}>{comp.status}</Badge>
          {comp.eid && <span className="text-[13px] text-muted">EID {comp.eid}</span>}
        </div>

        {result.events?.map((e) => (
          <div key={e.id} className="mt-3 border-t border-line pt-3">
            <Link
              to={link(`/competitions/${comp.id}/events/${e.id}`)}
              className="text-[13px] font-bold text-ink hover:text-primary hover:underline"
            >
              {e.name} — {e.date} · grand aggregate →
            </Link>
            <div className="mt-2 flex flex-wrap gap-2">
              {result.matches?.map((m) => (
                <Link
                  key={m.id}
                  to={link(`/competitions/${comp.id}/events/${e.id}/matches/${m.id}`)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-[13px] font-semibold text-primary transition-colors hover:bg-primary-soft"
                >
                  <Target size={13} /> {m.name} ({m.shots} shots)
                </Link>
              ))}
            </div>
          </div>
        ))}

        {!!result.shooters?.length && (
          <div className="mt-3 border-t border-line pt-3">
            <div className="flex items-center gap-1.5 text-[13px] font-bold text-ink">
              <Users size={14} className="text-muted" /> Competitors registered from the Shoot sheet
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {result.shooters.map((s) => (
                <Link
                  key={`${s.sid}-${s.name}`}
                  to={link(`/shooters/${s.sid}`)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2.5 py-1 text-[13px] text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <span className="font-semibold">{s.name}</span>
                  <span className="text-muted">
                    {s.sid} · {s.classes.join("/") || "—"} · {s.grade}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function ImportData() {
  const link = usePortalLink();
  const [tab, setTab] = useState(TABS.workbook);
  const { data: importHistory, reload } = useApi<ImportRecord[]>("/imports", []);
  const { data: competitions } = useApi<Competition[]>("/competitions", []);
  const { data: matches } = useApi<MatchInfo[]>("/matches", []);

  const [file, setFile] = useState<File | null>(null);
  const [competitionId, setCompetitionId] = useState("");
  const [matchId, setMatchId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);
  /*
   * The name of the file that just went in. The box clears itself after a
   * successful import — a finished file left sitting in the dropzone with a
   * Remove button beside it read as "remove what you just imported", and during
   * a day of re-uploads people clicked it, lost their place, and started again.
   * Keeping the name lets the empty box say what it did.
   */
  const [imported, setImported] = useState<string | null>(null);
  /* Set while the "undo" confirmation for the upload just made is open. */
  const [undoing, setUndoing] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const isWorkbook = tab === TABS.workbook;
  /*
   * A shoot arrives as one of two files on the same dropzone: the Shoot
   * Information workbook, or the shot log the target server writes as CSV once
   * the shoot is finished. Which one it is, is decided by the file rather than
   * by a tab the operator has to choose first — they are two halves of the same
   * job, and the backend already routes on the extension.
   */
  const isCsvLog = !!file && /\.csv$/i.test(file.name);
  const acceptTypes = isWorkbook ? ".xlsx,.xls,.csv" : tab === "JSON Shotslog" ? ".json" : ".csv";

  const pickFile = (f: File | null) => {
    setFile(f);
    setResult(null);
    setImported(null);
    setError("");
    // Without this, picking the same file twice in a row is silent — the input
    // holds the old value and fires no change. A corrected re-upload of the
    // same filename is exactly what this page is used for.
    if (fileInput.current) fileInput.current.value = "";
  };

  const switchTab = (t: string) => {
    setTab(t);
    pickFile(null);
  };

  const runImport = async () => {
    if (!file) {
      setError("Choose a file first.");
      return;
    }
    if (!adminSession.token()) {
      setError("You must sign in first (admin / admin123) — imports are authenticated.");
      return;
    }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const comp = competitions.find((c) => c.id === competitionId);
      // The workbook carries its own competition, matches and competitors, so the
      // sidebar selections are only sent for the shotslog/CSV formats.
      const asWorkbook = isWorkbook && !isCsvLog;
      const body = await apiUpload<{ data: ImportResult }>(
        asWorkbook ? "/admin/import/workbook" : "/admin/import/shotslog",
        file,
        asWorkbook ? {} : { competitionId, competitionName: comp?.name || "", ...(matchId ? { matchId } : {}) }
      );
      setResult(body.data);
      /*
       * Done means done: the file leaves the box and the report stays. Nothing
       * is left to click except choosing the next file.
       */
      if (body.data?.status !== "Failed") {
        setImported(file.name);
        setFile(null);
        if (fileInput.current) fileInput.current.value = "";
      }
      reload();
    } catch (e: any) {
      setError(e.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  /*
   * Undo the upload that just ran.
   *
   * The same removal Import History performs, offered where the mistake is
   * noticed: a wrong file is spotted in the report, not an hour later. It takes
   * out everything this upload wrote — its shot strings, and any range, day or
   * event left holding nothing — so the corrected spreadsheet goes onto clean
   * ground instead of mixing with what the bad one left behind.
   */
  const undoImport = async () => {
    if (!result?.importId) return;
    setUndoBusy(true);
    setError("");
    try {
      await apiDelete<{ message: string }>(`/admin/imports/${encodeURIComponent(result.importId)}`);
      setResult(null);
      setImported(null);
      setUndoing(false);
      reload();
    } catch (e: any) {
      setError(e.message || "Could not undo that import");
      setUndoing(false);
    } finally {
      setUndoBusy(false);
    }
  };

  const warnings = result?.warnings ?? result?.errors ?? [];

  return (
    <div>
      <AdminTopbar title="Import Data — event details and results" subtitle="Import a complete shoot from the Excel workbook, or shot logs from the SMT C2 export." />

      <div className="card">
        {SHOW_TABS && <Tabs tabs={Object.values(TABS)} active={tab} onChange={switchTab} className="px-5 pt-2" />}
        <div className="grid gap-6 p-4 sm:p-6 xl:grid-cols-[1fr_340px_320px]">
          {/* Dropzone */}
          <div>
            <div className="flex items-start gap-2.5 rounded-lg bg-primary-soft px-4 py-3 text-[13px] text-slate-600">
              <Info size={15} className="mt-0.5 shrink-0 text-primary" />
              {isWorkbook ? (
                <span>
                  Upload the <strong>Shoot Information</strong> workbook and the whole shoot is built in one pass:{" "}
                  <strong>Event</strong> → <strong>Competition</strong> (each CID) → <strong>Match</strong> (one range per MID) →{" "}
                  <strong>Shooter</strong> (competitors, matched on their permanent SID) → <strong>Shotslog</strong> (shot strings).
                  <br />
                  Match results, grand aggregates and rankings are generated automatically. Re-uploading the same workbook
                  updates in place — it never duplicates.
                  <br />
                  Once the meeting is loaded you can drop the target server's <strong>shotslog .csv</strong> here on its own
                  — it finds its own event from its EID and MID columns, so there is no need to paste it into the workbook.
                </span>
              ) : tab === "JSON Shotslog" ? (
                <span>
                  Import shot data from JSON (.json) shotslog files exported by the SMT C2.
                  <br />
                  Strings are routed to a match by target face distance and shooters matched on their SID.
                </span>
              ) : (
                <span>
                  Import flat CSV rows with <code>sid, match, string, shot, score, x, y, velocity</code> columns.
                  <br />
                  Choose the target competition and match on the right.
                </span>
              )}
            </div>

            <div
              className={`mt-5 flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-5 py-7 text-center transition-colors ${
                dragging ? "border-primary bg-primary-soft/50" : "border-slate-300 hover:border-primary/60 hover:bg-primary-soft/30"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                pickFile(e.dataTransfer.files?.[0] ?? null);
              }}
            >
              {imported && !file ? (
                <CheckCircle2 size={52} className="text-success" strokeWidth={1.25} />
              ) : (
                <CloudUpload size={52} className="text-slate-400" strokeWidth={1.25} />
              )}
              {file ? (
                <>
                  <p className="mt-4 flex items-center gap-2 text-lg font-bold text-ink">
                    {file.name.endsWith(".json") ? <Braces size={18} className="text-primary" /> : <FileSpreadsheet size={18} className="text-success" />}
                    {file.name}
                  </p>
                  <p className="mt-1 text-sm text-muted">{(file.size / 1024).toFixed(1)} KB — ready to import</p>
                  <div className="mt-4 flex gap-3">
                    <Button onClick={runImport} disabled={busy}>
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <CloudUpload size={16} />}
                      {busy ? "Importing..." : isWorkbook ? "Import Workbook" : "Start Import"}
                    </Button>
                    {/*
                     * Gone while the upload runs. "Remove" beside a progress
                     * spinner looks like a way to stop it, and it is not.
                     * It also says what it removes: the chosen file, not data.
                     */}
                    {!busy && (
                      <Button variant="secondary" onClick={() => pickFile(null)}>
                        Remove this file
                      </Button>
                    )}
                  </div>
                </>
              ) : imported ? (
                <>
                  {/* What just happened, and the one thing left to do. */}
                  <p className="mt-4 flex items-center gap-2 text-lg font-bold text-ink">
                    <FileSpreadsheet size={18} className="text-success" />
                    {imported}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-success">Imported — the report is below.</p>
                  <button
                    onClick={() => fileInput.current?.click()}
                    className="mt-3 h-10 rounded-lg bg-primary px-6 text-sm font-bold text-white transition-colors hover:bg-primary-hover"
                  >
                    Import next file
                  </button>
                  <p className="mt-2 text-[13px] text-muted">or drop the next file here</p>
                </>
              ) : (
                <>
                  <p className="mt-4 text-lg font-bold text-ink">Drag and drop your file here</p>
                  <p className="mt-1 text-sm text-muted">or</p>
                  <button
                    onClick={() => fileInput.current?.click()}
                    className="mt-3 h-10 rounded-lg bg-primary px-6 text-sm font-bold text-white transition-colors hover:bg-primary-hover"
                  >
                    Choose File
                  </button>
                </>
              )}
              <input ref={fileInput} type="file" accept={acceptTypes} className="hidden" onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
            </div>
            <p className="mt-4 text-center text-[13px] text-muted">
              Supported formats: {acceptTypes.split(",").join(" ")} · Maximum file size: 50 MB
            </p>

            {error && (
              <div className="mt-4 flex items-center gap-2.5 rounded-lg bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">
                <AlertCircle size={16} className="shrink-0" /> {error}
              </div>
            )}

            {result && (
              <div className={`mt-4 rounded-xl border p-4 ${result.status === "Failed" ? "border-danger/30 bg-danger-soft/40" : "border-success/30 bg-success-soft/50"}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h4 className="flex items-center gap-2 text-[15px] font-bold text-ink">
                    {result.status === "Failed" ? <AlertCircle size={18} className="text-danger" /> : <CheckCircle2 size={18} className="text-success" />}
                    Import {result.status} — {result.importId}
                  </h4>
                  {/*
                   * The way back, where the mistake is seen. Wrong file, wrong
                   * match, wrong day: undo it here and upload the corrected one,
                   * rather than deleting ranges by hand and discovering the old
                   * data comes back with them.
                   */}
                  {!!result.importId && (
                    <button
                      onClick={() => setUndoing(true)}
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-danger/40 bg-white px-3 text-[13px] font-bold text-danger transition-colors hover:bg-danger-soft"
                    >
                      <Undo2 size={14} /> Undo this import
                    </button>
                  )}
                </div>

                {result.kind === "workbook" && result.counts ? (
                  <WorkbookReport result={result} />
                ) : (
                  <>
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <Stat value={result.shotsImported} label="Shots Imported" />
                      <Stat value={result.stringsCreated} label="Strings Created" />
                      <Stat value={result.matchesUpdated?.length ?? 0} label="Matches Updated" />
                    </div>
                    {!!result.matchesUpdated?.length && (
                      <div className="mt-3 text-[13px] text-slate-600">
                        Updated leaderboards:{" "}
                        {result.matchesUpdated.map((m) => (
                          <Link key={m} to={`/competitions/${competitionId}/events/e1/matches/${m}`} className="mr-2 font-bold text-primary hover:underline">
                            {m} results →
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {!!result.stringIds?.length && (
                  <div className="mt-3">
                    <div className="text-[13px] font-bold text-ink">Target maps ({result.stringIds.length} strings):</div>
                    <div className="mt-1.5 flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                      {result.stringIds.map((id) => (
                        <Link
                          key={id}
                          to={link(`/strings/${id}`)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-[12px] font-semibold text-primary transition-colors hover:bg-primary-soft"
                        >
                          <Target size={13} /> {id}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {!!warnings.length && (
                  <div className="mt-3 rounded-lg bg-white p-3">
                    <div className="flex items-center gap-1.5 text-[13px] font-bold text-warning">
                      <AlertTriangle size={14} /> Import report — {warnings.length} item(s) needing a look
                    </div>
                    <ul className="mt-1.5 max-h-60 space-y-1 overflow-y-auto text-[13px] leading-snug text-slate-600">
                      {warnings.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Import options */}
          <div className="rounded-xl border border-line p-4">
            <h3 className="text-[15px] font-bold text-ink">Import Options</h3>
            {isWorkbook ? (
              <div className="mt-4 space-y-3 text-[13px] leading-relaxed text-slate-600">
                <p>
                  The workbook is self-describing — the competition, ranges and competitor list all come from the file, so
                  there is nothing to choose here.
                </p>
                <dl className="space-y-2 rounded-lg bg-slate-50 p-3">
                  {[
                    ["Event sheet", "Event name, EID, associations, host club, range, officials"],
                    ["Competition sheet", "Each CID and its name — the trophies shot within the event"],
                    ["Match sheet", "One range per row: order, date, CID, MID, distance, sighters, shots"],
                    ["Shooter sheet", "Competitors keyed by SID — a shooter may be entered in more than one class"],
                    ["Shotslog sheet", "Shot strings keyed by EID / MID / SID, scored 5–1 + V (TR) / 6–1 + X (F-class)"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <dt className="font-bold text-ink">{k}</dt>
                      <dd className="text-muted">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-muted">
                  Shot strings are keyed by the log's EID / MID / SID columns — a string whose SID is not on the Shoot
                  sheet is skipped and listed in the import report. The typed label is kept as a cross-check.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="label">
                    Event <span className="text-danger">*</span>
                  </label>
                  <select className="input appearance-none pr-8 font-medium" value={competitionId} onChange={(e) => setCompetitionId(e.target.value)}>
                    {competitions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Match (Optional — read from file when present)</label>
                  <select className="input appearance-none pr-8 font-medium" value={matchId} onChange={(e) => setMatchId(e.target.value)}>
                    <option value="">Use match from file</option>
                    {matches.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2.5 pt-1">
                  {["Update existing records", "Skip rows with errors", "Validate data before import"].map((o) => (
                    <label key={o} className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-700">
                      <input type="checkbox" defaultChecked className="h-4 w-4 rounded accent-primary" /> {o}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Workbook format reference */}
          <div className="rounded-xl border border-line p-4">
            <h3 className="text-[15px] font-bold text-ink">Workbook Format</h3>
            <p className="mt-1.5 text-[13px] leading-snug text-muted">
              Sheets are matched by name and columns by their headers, so extra columns, reordered columns and blank rows
              are all tolerated.
            </p>
            <dl className="mt-3 space-y-3 text-[13px]">
              {[
                ["Event", "Association Intl / National / State, Host club, Event Name, Event Level, Event Type, State, Location, Range, Match Director, Chief Range Officer, EID"],
                ["Competition", "Order, EID, CID, Competition name — one row per trophy shot within the event"],
                ["Match", "Order, Date, CID, MID, Name, Distance, mtrs or yds, Sighters, Convertible, Shots — one row per range"],
                ["Shooter", "SID, Name, Club ID, Club Name, State, Country, Gender, Age, Group, Team, Category, Position, Rifle Class, Grade — one row per class entered"],
                ["Shotslog", "Blocks keyed by EID / MID / SID: a header row (string label, Class, frame, face, calibration) followed by one row per shot"],
              ].map(([sheet, cols]) => (
                <div key={sheet}>
                  <dt className="flex items-center gap-2 font-bold text-ink">
                    <FileSpreadsheet size={14} className="text-success" /> {sheet}
                  </dt>
                  <dd className="mt-0.5 leading-snug text-muted">{cols}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 border-t border-line pt-3 text-[13px] leading-snug text-muted">
              Scoring follows ICFRA fullbore: <b className="text-ink">TR</b> 5–1 with a V centre,{" "}
              <b className="text-ink">F-class</b> 6–1 with an X centre. Coordinates arrive in millimetres and are converted
              to MOA using the range distance.
            </p>
          </div>
        </div>
      </div>

      {/* Import history */}
      <div className="card mt-4">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
            <RefreshCw size={16} className="text-muted" /> Import History
          </h3>
          <Button variant="secondary" size="sm" onClick={reload}>
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-line bg-slate-50">
                <th className="th">File Name</th>
                <th className="th">Data Type</th>
                <th className="th">Event</th>
                <th className="th text-right">Records</th>
                <th className="th">Imported By</th>
                <th className="th">Date &amp; Time</th>
                <th className="th">Status</th>
                <th className="th text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {importHistory.slice(0, 5).map((r) => (
                <tr key={r.id} className="transition-colors hover:bg-slate-50">
                  <td className="td">
                    <span className="flex items-center gap-2.5 font-semibold">
                      {r.fileType === "json" ? <Braces size={16} className="text-primary" /> : <FileSpreadsheet size={16} className="text-success" />}
                      {r.fileName}
                    </span>
                  </td>
                  <td className="td">{r.dataType}</td>
                  <td className="td text-slate-600">{r.competition}</td>
                  <td className="td text-right font-semibold">{r.records.toLocaleString()}</td>
                  <td className="td text-muted">{r.importedBy}</td>
                  <td className="td text-muted">
                    {r.date} {r.time}
                  </td>
                  <td className="td">
                    <ImportStatusBadge status={r.status} />
                  </td>
                  <td className="td text-right">
                    {/* Import History is where an upload can be inspected in full
                        or removed, so the row action goes there. */}
                    <Link
                      to="/admin/import-history"
                      className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-semibold text-primary transition-colors hover:bg-primary-soft"
                    >
                      Details <ChevronRight size={13} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-line">
          <Pagination summary={`Showing 1 to ${Math.min(5, importHistory.length)} of ${importHistory.length} imports`} pages={Math.max(1, Math.ceil(importHistory.length / 5))} />
        </div>
      </div>

      <ConfirmDialog
        open={undoing}
        busy={undoBusy}
        title="Undo this import?"
        intro="This removes everything that upload added — its shot strings, and any range, day or event left holding nothing."
        items={result ? [`${imported || result.importId} — ${result.importId}`] : []}
        note={
          <>
            Anything another upload contributed stays, and <b>competitors stay on the register</b>. Scores and rankings are
            recalculated from what remains, so the corrected file can be uploaded onto clean ground.
          </>
        }
        confirmLabel="Undo import"
        onConfirm={undoImport}
        onCancel={() => setUndoing(false)}
      />
    </div>
  );
}
